import { useApi } from '@gravitate-js/the-armory'
import { getAPIURL } from '@utils/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UpsertRequest, DeliveryRequestOverviewRequest } from './request'

export const useDeliveryRequests = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()
  const getDeliveryRequestsQuery = (filters: DeliveryRequestOverviewRequest) =>
    useQuery({
      queryKey: ['deliveryRequestsQuery', filters],
      queryFn: () => api.post('delivery_request/overview', filters),
      enabled: !!filters,
    })

  const upsertRequestMutation = useMutation({
    mutationKey: ['upsertRequestMutation'],
    mutationFn: (request: UpsertRequest) => api.post(`delivery_request/upsert`, request),
    onSuccess: async (response, request) => {
      queryClient.setQueriesData({ queryKey: ['deliveryRequestsQuery'] }, (oldData) => {
        if (!request.id) {
          return {
            ...oldData,
            rows: [response, ...oldData.rows],
          }
        }
        return {
          ...oldData,
          rows: oldData.rows.map((row) => (response.id === row.id ? { ...row, ...response } : row)),
        }
      })
    },
  })

  const cancelRequestMutation = useMutation({
    mutationKey: ['cancelRequestMutation'],
    mutationFn: (payload: { id: string; note: string }) =>
      api.post(`delivery_request/cancel`, { request_id: payload.id, note: payload.note }),
    onSuccess: async (response, request) => {
      await queryClient.invalidateQueries({
        queryKey: ['deliveryRequestsQuery'],
      })
    },
  })

  return {
    getDeliveryRequestsQuery,
    upsertRequestMutation,
    cancelRequestMutation,
  }
}
